/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable require-await */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable jsx-a11y/label-has-associated-control */

import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import React, { useMemo } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { MdCheckCircle, MdOutlineCheckCircle } from 'react-icons/md';
import Modal from 'react-modal';

import Container from '../../../../Components/Container/Container';
import { GroupMember, GroupSummary } from '../../../../services/patient_plan/types/types';
import { clamp, roleText } from '../../assets/helpers';
import styles from './styles.module.scss';

type Usage = { used: number; total: number };

type Props = {
  isOpen: boolean;
  onClose: () => void;

  group: GroupSummary | null; // plan limits / names
  member: GroupMember | null; // person being viewed
  usage?: Usage; // per-member usage if available
  onRequestRemove?: (member: GroupMember) => void; // <— NEW: ask parent to open confirm modal
  onViewHistory?: (memberId: number) => void;
};

const initials = (first?: string | null, last?: string | null, email?: string | null): string => {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  const token = (f || l || email || '—').slice(0, 2);
  return token.toUpperCase();
};

const fullName = (m?: GroupMember | null): string =>
  m ? [m.first_name, m.last_name].filter(Boolean).join(' ') || (m as GroupMember).email || `User #${m.id}` : '';

const MemberDetailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  group,
  member,
  usage,
  onRequestRemove,
  onViewHistory,
}) => {
  const isMobile = useWindowSize().width < 768;
  const { userInfo } = useAppSelector(selectAuth);

  const mAny = (member || {}) as GroupMember;

  const unlimited: boolean = Boolean(group?.unlimited) || Boolean(mAny?.unlimited);

  const totalRaw =
    (typeof mAny?.free_total === 'number' ? mAny.free_total : undefined) ??
    (typeof usage?.total === 'number' ? usage.total : undefined) ??
    (typeof group?.free_cases_per_year === 'number' ? group?.free_cases_per_year : 0);

  const total: number = unlimited ? 0 : Math.max(0, Number(totalRaw) || 0);

  let used = 0;
  if (!unlimited && typeof mAny?.free_remaining === 'number' && typeof total === 'number') {
    used = clamp(total - mAny.free_remaining, 0, total);
  } else if (!unlimited && typeof usage?.used === 'number') {
    used = clamp(usage.used, 0, total);
  } else {
    used = 0;
  }

  const dotKeys = Array.from({ length: total }, (_, i) => `d-${i}`);

  const hideCareHistory = useMemo(() => {
    const currentUserId = userInfo?.id;
    if (!currentUserId) return true;

    if (member?.id === currentUserId) return false;

    const foundMember = group?.members?.find((m: GroupMember) => m.id === member?.id);
    const currentMember = group?.members?.find((m: GroupMember) => m.id === currentUserId);

    const isDependent = foundMember?.role === 'DEPENDENT';
    const isOwner = currentMember?.role === 'OWNER';

    return !(isDependent && isOwner);
  }, [userInfo, group, member]);

  const handleHistory = () => {
    if (member?.id) onViewHistory?.(member.id);
  };

  const name = fullName(member);

  const DangerCard = () =>
    member?.role !== 'OWNER' ? (
      <div className={styles.dangerCard} role="group" aria-label="Remove from plan">
        <div className={styles.dangerTitle}>Remove from plan</div>
        <p className={styles.dangerText}>
          If the user you&apos;re removing has already used any services, you cannot replace them with a new member.
        </p>
        <button
          type="button"
          className={styles.dangerBtn}
          onClick={() => member && onRequestRemove?.(member)}
          aria-label="Remove from plan"
        >
          Remove from plan
        </button>
      </div>
    ) : null;

  if (isMobile) {
    return isOpen ? (
      <Container
        patientDashboard
        isSidenavVisible
        childClassName={styles.mobileWrap}
        containerStyle={{ justifyContent: 'start', overflowY: 'auto', zIndex: 1000 }}
      >
        <div className={styles.mobileCard}>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close" type="button">
            <AiOutlineClose />
          </button>

          <div className={styles.headerBlock}>
            <div className={styles.avatarLg}>
              <span>{initials(member?.first_name, member?.last_name, (member as GroupMember)?.email)}</span>
            </div>
            <div className={styles.name}>{name}</div>
            <div className={styles.role}>{roleText(member)}</div>
          </div>

          <div className={styles.badgesRow}>
            {!unlimited &&
              dotKeys.map((k, i) =>
                i < used ? (
                  <MdCheckCircle key={k} className={styles.badgeUsed} />
                ) : (
                  <MdOutlineCheckCircle key={k} className={styles.badgeFree} />
                ),
              )}
          </div>

          <p className={styles.usageLine}>
            {unlimited ? (
              <>
                <strong>{member?.first_name || name.split(' ')[0] || 'This member'}</strong> has unlimited free services
                with this plan
              </>
            ) : group?.free_cases_per_year && group?.free_cases_per_year > 0 ? (
              <>
                <strong>{member?.first_name || name.split(' ')[0] || 'This member'}</strong> has used {used} of {total}{' '}
                free services that come with this plan
              </>
            ) : null}
          </p>
          {!hideCareHistory && (
            <div className={styles.historyContainer}>
              <button type="button" className={styles.historyLink} onClick={handleHistory}>
                Care History
              </button>
            </div>
          )}

          <DangerCard />
        </div>
      </Container>
    ) : null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
      ariaHideApp={false}
    >
      <button className={styles.closeBtn} onClick={onClose} aria-label="Close" type="button">
        <AiOutlineClose />
      </button>

      <div className={styles.headerBlock}>
        <div className={styles.avatarLg}>
          <span>{initials(member?.first_name, member?.last_name, (member as GroupMember)?.email)}</span>
        </div>
        <div className={styles.name}>{name}</div>
        <div className={styles.role}>{roleText(member)}</div>
      </div>

      <div className={styles.badgesRow}>
        {!unlimited &&
          dotKeys.map((k, i) =>
            i < used ? (
              <MdCheckCircle key={k} className={styles.badgeUsed} />
            ) : (
              <MdOutlineCheckCircle key={k} className={styles.badgeFree} />
            ),
          )}
      </div>

      <p className={styles.usageLine}>
        {unlimited ? (
          <>
            <strong>{member?.first_name || name.split(' ')[0] || 'This member'}</strong> has unlimited free services
            with this plan
          </>
        ) : group?.free_cases_per_year && group?.free_cases_per_year > 0 ? (
          <>
            <strong>{member?.first_name || name.split(' ')[0] || 'This member'}</strong> has used {used} of {total} free
            services that come with this plan
          </>
        ) : null}
      </p>
      {!hideCareHistory && (
        <div className={styles.historyContainer}>
          <button type="button" className={styles.historyLink} onClick={handleHistory}>
            Care History
          </button>
        </div>
      )}
      <DangerCard />
    </Modal>
  );
};

export default MemberDetailsModal;
